import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import fleetApi from '../../services/api.service';
import { buildUrlWithParams } from '../../utils/url.utils';
import { adaptConsignmentRulesListResponse } from '../../utils/consignment-rule.util';

export default function useGetConsignmentRules(filters, delay = 300) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const debounceTimeout = useRef(null);
  const abortRef = useRef(null);

  const page = Math.floor(Number(filters.offset) / Number(filters.limit)) + 1;

  const fetchData = useCallback(async () => {
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);
    try {
      const url = buildUrlWithParams('/consignment-rule', {
        page,
        limit: filters.limit,
        item_key: filters.item_key || undefined,
        packaging_id: filters.packaging_id || undefined,
      });
      const response = await fleetApi.get(url, { signal: controller.signal });

      if (!controller.signal.aborted) {
        setData(adaptConsignmentRulesListResponse(response.data));
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setError(
          err.response?.data?.message ||
            err.message ||
            'Une erreur est survenue'
        );
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [page, filters.limit, filters.item_key, filters.packaging_id]);

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      fetchData();
    }, delay);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [fetchData, delay]);

  const mutate = useMemo(() => fetchData, [fetchData]);

  return { data, isLoading, error, mutate };
}
